
import React, { useState } from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { CardGrid } from "@/components/cards/CardGrid";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SearchBar } from "@/components/cards/SearchBar";

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const handleNavigate = (path: string) => {
    // Scroll to top before navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(path);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  return (
    <PageContainer>
      {/* Hero */}
      <div className="relative bg-pokemon-blue rounded-2xl overflow-hidden mb-8">
        <div className="absolute inset-0 bg-gradient-to-r from-pokemon-blue/90 to-pokemon-blue/20"></div>
        <div className="relative z-10 py-16 px-6 md:px-12 flex flex-col md:flex-row items-center justify-between">
          <div className="text-white mb-8 md:mb-0">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Your Card's Value - Instantly
            </h1>
            <p className="text-white/80 text-lg mb-6 max-w-md">
              Discover the market value of any Pokémon card in your collection
              with our Pokémon card scanner.
            </p>
            
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => handleNavigate("/scan")}
                className="bg-white text-pokemon-blue hover:bg-white/90"
                size="lg"
              >
                <Camera className="mr-2 h-5 w-5" />
                Scan Card
              </Button>
              <Button
                onClick={() => handleNavigate("/collection")}
                variant="outline"
                className="border-white text-pokemon-yellow hover:bg-white/20 hover:text-white"
                size="lg"
              >
                View Collection
              </Button>
            </div>
          </div>
          <motion.div 
            className="w-full max-w-xs"
            animate={{ 
              y: [0, -10, 0],
              rotateY: [0, 5, 0],
              rotateZ: [2, 4, 2]  // Keep card slightly tilted with bottom to the right
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 6, 
              ease: "easeInOut" 
            }}
          >
            <div className="card-3d-effect">
              <div className="card-rainbow-shine">
                <motion.img
                  src="https://assets.pokemon.com/assets/cms2/img/cards/web/SM9/SM9_EN_190.png"
                  alt="Featured Pokémon card"
                  className="w-full h-auto shadow-xl rounded-lg"
                  whileHover={{ scale: 1.02 }}
                  style={{ transformStyle: 'preserve-3d', transform: 'rotateZ(2deg)' }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Search Bar - Moved above featured cards */}
      <div className="mb-6">
        <SearchBar onSearch={handleSearch} />
      </div>

      {/* Card Grid */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Featured Cards</h2>
        <CardGrid pageSize={10} initialQuery={searchQuery} />
      </div>
    </PageContainer>
  );
};

export default HomePage;
